import React, { useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import usePortal from 'react-useportal';
import { fixBodyScroll, unfixBodyScroll } from 'lib/browser';
import Dialog from './Dialog';

// The use of NULL_EVENT is a workaround until this issue is closed https://github.com/alex-cory/react-useportal/issues/36
const NULL_EVENT = {
  currentTarget: {
    contains: () => false,
  },
};

export const useDialog = (options = {}) => {
  const { initialiseOpen = false, closeOnEsc = false, closeOnOutsideClick = false } = options;

  const { openPortal, closePortal, isOpen, Portal } = usePortal({
    closeOnEsc,
    closeOnOutsideClick,
  });

  const openDialog = useCallback(
    (event) => {
      fixBodyScroll();
      openPortal(event || NULL_EVENT);
    },
    [openPortal],
  );

  const closeDialog = useCallback(
    (event) => {
      unfixBodyScroll();
      closePortal(event || NULL_EVENT);
    },
    [closePortal],
  );

  useEffectOnce(() => {
    if (initialiseOpen) openDialog();

    return () => unfixBodyScroll();
  }, []);

  const DialogComponent = useCallback(({ ...rest }) => <Dialog Portal={Portal} isOpen={isOpen} {...rest} />, [Portal, isOpen]);

  return {
    Dialog: DialogComponent,
    isOpen,
    openDialog,
    closeDialog,
  };
};
